import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Modal, Box, Typography, Button } from "@mui/material";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faSearch,
  faEdit,
  faPlus,
  faImage,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import "../screenstyles/OffersList.css";

interface Offer {
  id: string;
  ImageId: string;
  dateupload: any;
  offerforwordid: string;
  offername: string;
  offertype: string;
  overdescription: string;
}

interface OfferFormState {
  offerName: string;
  offerType: string;
  offerDescription: string;
  dateUpload: string;
  image: File | null;
  preview: string | null;
}

const emptyOfferForm: OfferFormState = {
  offerName: "",
  offerType: "",
  offerDescription: "",
  dateUpload: "",
  image: null,
  preview: null,
};

const formatDateForInput = (value: any) => {
  if (!value) return "";

  if (value?.seconds) {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === "string" && value !== "غير متوفر") {
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().slice(0, 10);
    }
  }

  return "";
};

const formatDateForDisplay = (value: any) => {
  if (!value) return "غير متوفر";

  if (value?.seconds) {
    return new Date(value.seconds * 1000).toLocaleDateString();
  }

  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  return String(value);
};

const OffersManagement: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [formState, setFormState] = useState<OfferFormState>(emptyOfferForm);
  const [savingOffer, setSavingOffer] = useState(false);

  const storage = getStorage();

  const fetchOffers = async () => {
    setLoading(true);

    try {
      const offersCollection = collection(db, "offers");
      const offersDocs = await getDocs(offersCollection);

      const fetchedOffers: Offer[] = [];

      offersDocs.forEach((offerDoc) => {
        const data = offerDoc.data();

        fetchedOffers.push({
          id: offerDoc.id,
          ImageId: data.ImageId || "",
          dateupload: data.dateupload || null,
          offerforwordid: data.offerforwordid || "غير متوفر",
          offername: data.offername || "بدون عنوان",
          offertype: data.offertype || "غير محدد",
          overdescription: data.overdescription || "لا يوجد وصف",
        });
      });

      setOffers(fetchedOffers);
    } catch (error) {
      console.error("❌ Error fetching offers:", error);
      toast.error("حدث خطأ أثناء تحميل العروض");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const filteredOffers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return offers;

    return offers.filter((offer) => {
      return (
        offer.offername.toLowerCase().includes(q) ||
        offer.offertype.toLowerCase().includes(q) ||
        offer.overdescription.toLowerCase().includes(q)
      );
    });
  }, [offers, searchQuery]);

  const resetForm = () => {
    setFormState(emptyOfferForm);
    setEditingOffer(null);
  };

  const openAddModal = () => {
    resetForm();
    setFormModalOpen(true);
  };

  const openEditModal = (offer: Offer) => {
    setEditingOffer(offer);
    setFormState({
      offerName: offer.offername || "",
      offerType: offer.offertype || "",
      offerDescription: offer.overdescription || "",
      dateUpload: formatDateForInput(offer.dateupload),
      image: null,
      preview: offer.ImageId || null,
    });
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (savingOffer) return;
    setFormModalOpen(false);
    resetForm();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    setFormState((prev) => ({
      ...prev,
      image: file,
      preview: file ? URL.createObjectURL(file) : editingOffer?.ImageId || null,
    }));
  };

  const uploadOfferImageIfNeeded = async () => {
    if (!formState.image) {
      return editingOffer?.ImageId || "";
    }

    const safeFileName = `${Date.now()}-${formState.image.name}`;
    const imageRef = ref(storage, `/Images/offers/${safeFileName}`);

    await uploadBytes(imageRef, formState.image);
    return await getDownloadURL(imageRef);
  };

  const handleSaveOffer = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formState.offerName.trim() ||
      !formState.offerType.trim() ||
      !formState.offerDescription.trim() ||
      !formState.dateUpload
    ) {
      toast.error("يرجى ملء جميع الحقول", {
        duration: 3000,
        position: "top-right",
        className: "offers-toast-error",
      });
      return;
    }

    if (!editingOffer && !formState.image) {
      toast.error("يرجى اختيار صورة للعرض", {
        duration: 3000,
        position: "top-right",
        className: "offers-toast-error",
      });
      return;
    }

    setSavingOffer(true);

    try {
      const imageUrl = await uploadOfferImageIfNeeded();

      const payload = {
        offername: formState.offerName.trim(),
        offertype: formState.offerType.trim(),
        overdescription: formState.offerDescription.trim(),
        dateupload: Timestamp.fromDate(new Date(formState.dateUpload)),
        ImageId: imageUrl,
      };

      if (editingOffer) {
        await updateDoc(doc(db, "offers", editingOffer.id), payload);

        toast.success("تم تعديل العرض بنجاح!", {
          duration: 3000,
          position: "top-right",
          className: "offers-toast-success",
        });
      } else {
        await addDoc(collection(db, "offers"), payload);

        toast.success("تم إضافة العرض بنجاح!", {
          duration: 3000,
          position: "top-right",
          className: "offers-toast-success",
        });
      }

      await fetchOffers();
      closeFormModal();
    } catch (error) {
      console.error("❌ خطأ أثناء حفظ العرض:", error);
      toast.error("حدث خطأ أثناء حفظ العرض", {
        duration: 3000,
        position: "top-right",
        className: "offers-toast-error",
      });
    } finally {
      setSavingOffer(false);
    }
  };

  const openDeleteModal = (id: string) => {
    setSelectedOfferId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedOfferId(null);
    setDeleteModalOpen(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, "offers", id));

      toast.success("تم حذف العرض بنجاح!", {
        duration: 3000,
        position: "top-right",
        className: "offers-toast-success",
      });

      await fetchOffers();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting offer:", error);
      toast.error("حدث خطأ أثناء حذف العرض");
    }
  };

  return (
    <div className="offers-page" dir="rtl">
      <div className="offers-header">
        <h1 className="offers-title">إدارة العروض</h1>

        <div className="offers-search-box">
          <FontAwesomeIcon icon={faSearch} className="offers-search-icon" />
          <input
            type="text"
            placeholder="ابحث باسم العرض أو النوع أو الوصف..."
            className="offers-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="offers-header-action-btn"
          onClick={openAddModal}
          title="إضافة عرض جديد"
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>إضافة عرض</span>
        </button>
      </div>

      {loading ? (
        <div className="offers-loading">جاري تحميل البيانات...</div>
      ) : (
        <div className="offers-table-container">
          <table className="offers-table">
            <thead>
              <tr>
                <th>#</th>
                <th>الصورة</th>
                <th>اسم العرض</th>
                <th>الوصف</th>
                <th>نوع العرض</th>
                <th>تاريخ التحميل</th>
                <th>الإجراءات</th>
              </tr>
            </thead>

            <tbody>
              {filteredOffers.map((offer, index) => (
                <tr key={offer.id}>
                  <td>{index + 1}</td>

                  <td>
                    {offer.ImageId ? (
                      <img
                        src={offer.ImageId}
                        alt={offer.offername}
                        className="offers-image"
                      />
                    ) : (
                      <div className="offers-empty-image">لا توجد صورة</div>
                    )}
                  </td>

                  <td className="offers-cell-title">{offer.offername}</td>

                  <td className="offers-description-cell">
                    <div className="offers-description-text" title={offer.overdescription}>
                      {offer.overdescription}
                    </div>
                  </td>

                  <td>
                    <span className="offers-type-badge">{offer.offertype}</span>
                  </td>

                  <td className="offers-date-cell">{formatDateForDisplay(offer.dateupload)}</td>

                  <td>
                    <div className="offers-actions">
                      <button
                        type="button"
                        className="offers-action-btn offers-edit-btn"
                        onClick={() => openEditModal(offer)}
                        title="تعديل العرض"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>

                      <button
                        type="button"
                        className="offers-action-btn offers-delete-btn"
                        onClick={() => openDeleteModal(offer.id)}
                        title="حذف العرض"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredOffers.length === 0 && (
            <div className="offers-empty-state">لا توجد عروض مطابقة للبحث.</div>
          )}
        </div>
      )}

      <Modal open={formModalOpen} onClose={closeFormModal} className="offers-modal">
        <Box className="offers-form-modal-card">
          <div className="offers-form-modal-header">
            <div>
              <span className="offers-form-eyebrow">
                {editingOffer ? "تعديل عرض" : "إضافة عرض"}
              </span>
              <Typography className="offers-form-modal-title">
                {editingOffer ? "تعديل بيانات العرض" : "إضافة عرض جديد"}
              </Typography>
            </div>

            <button
              type="button"
              className="offers-form-close-btn"
              onClick={closeFormModal}
              disabled={savingOffer}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <form className="offers-form" onSubmit={handleSaveOffer}>
            <div className="offers-form-field">
              <label>اسم العرض</label>
              <input
                type="text"
                value={formState.offerName}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, offerName: e.target.value }))
                }
                placeholder="مثال: خصم كاميرات Hikvision"
              />
            </div>

            <div className="offers-form-field">
              <label>نوع العرض</label>
              <input
                type="text"
                value={formState.offerType}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, offerType: e.target.value }))
                }
                placeholder="مثال: خصم / إعلان / بانر"
              />
            </div>

            <div className="offers-form-field offers-form-full">
              <label>الوصف</label>
              <textarea
                value={formState.offerDescription}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    offerDescription: e.target.value,
                  }))
                }
                placeholder="اكتب وصف العرض..."
              />
            </div>

            <div className="offers-form-field">
              <label>تاريخ الإضافة</label>
              <input
                type="date"
                value={formState.dateUpload}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, dateUpload: e.target.value }))
                }
              />
            </div>

            <div className="offers-form-field offers-image-upload-field">
              <label>صورة العرض</label>

              <div className="offers-upload-row">
                <label className="offers-upload-btn">
                  <FontAwesomeIcon icon={faImage} />
                  اختيار صورة
                  <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                </label>

                {formState.preview && (
                  <img
                    src={formState.preview}
                    alt="معاينة العرض"
                    className="offers-form-preview"
                  />
                )}
              </div>
            </div>

            <div className="offers-form-actions offers-form-full">
              <button
                type="button"
                className="offers-form-cancel-btn"
                onClick={closeFormModal}
                disabled={savingOffer}
              >
                إلغاء
              </button>

              <button
                type="submit"
                className="offers-form-submit-btn"
                disabled={savingOffer}
              >
                {savingOffer
                  ? "جارٍ الحفظ..."
                  : editingOffer
                    ? "حفظ التعديلات"
                    : "إضافة العرض"}
              </button>
            </div>
          </form>
        </Box>
      </Modal>

      <Modal open={deleteModalOpen} onClose={closeDeleteModal} className="offers-modal">
        <Box className="offers-modal-card">
          <Typography className="offers-modal-title">
            هل أنت متأكد من أنك تريد حذف هذا العرض؟
          </Typography>

          <Box className="offers-modal-actions">
            <Button
              className="offers-modal-delete-btn"
              onClick={() => selectedOfferId && handleDelete(selectedOfferId)}
            >
              نعم، احذف العرض
            </Button>

            <Button className="offers-modal-cancel-btn" onClick={closeDeleteModal}>
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default OffersManagement;
